import { useCallback, useEffect, useMemo } from 'react';
import { ClaimContext, ClaimFileType, OnChangeClaimContext } from '../types';
import { useFileImport } from './useFileImport';

interface UseImportDocumentsProps {
  shopId: number;
  claim: ClaimContext;
  onChange: OnChangeClaimContext;
  isDORRequired: boolean;
  next: (params?: any) => void;
  extractInvoiceData: (fileId: number) => void;
}

export function useImportDocuments(props: UseImportDocumentsProps) {
  const { shopId, claim, onChange, next, isDORRequired, extractInvoiceData } = props;

  const [importInvoice, importInvoiceState] = useFileImport({
    shopId,
    trackingCode: claim.trackingCode,
    fileType: ClaimFileType.INVOICE_DOCUMENT,
    onChange,
    extractInvoiceData,
  });

  const [importDOR, importDORState] = useFileImport({
    shopId,
    shipmentId: claim.shipmentId,
    isDORRequired,
    fileType: ClaimFileType.DECLARATION_OF_RECIPIENT,
    onChange,
  });

  // Claims Center initiates file import after user entered tracking code and clicked "Next"
  const onSearchClaimNext = useCallback(() => {
    importInvoice();
    // Imports DOR only for investigation delivered
    if (isDORRequired) {
      importDOR();
    }

    next();
  }, [importInvoice, next]);

  // Parcel Finder initiates invoice import right away
  useEffect(() => {
    if (claim.entryPoint === 'parcel-finder') {
      importInvoice();
    }
  }, [claim.entryPoint, importInvoice]);

  // Parcel finder imports DOR right away for investigation delivered
  useEffect(() => {
    if (claim.entryPoint === 'parcel-finder' && isDORRequired) {
      importDOR();
    }
  }, [claim.entryPoint, importDOR, isDORRequired]);

  return { onSearchClaimNext, importDORState, importInvoiceState };
}
